import { ChainType, Token } from "@0xsquid/squid-types";
import { useCallback, useMemo } from "react";
import { SwapDirection } from "../../core/types/swap";
import { filterTokens, sortAllTokens } from "../../services";
import { useSquid } from "../squid/useSquid";
import { useConfigStore } from "../store/useSquidStore";

/**
 * Returns an array of the available tokens based on the provided config and direction
 */
export const useSquidTokens = (direction?: SwapDirection) => {
  const { tokens: allSquidTokens = [] } = useSquid();
  const config = useConfigStore((state) => state.config);

  const tokens = useMemo(() => {
    return filterTokens({
      tokens: allSquidTokens,
      config: {
        disabledChains: config.disabledChains,
        availableChains: config.availableChains,
      },
      direction,
    }).sort(sortAllTokens);
  }, [
    allSquidTokens,
    config.disabledChains,
    config.availableChains,
    direction,
  ]);

  const {
    evmTokens,
    cosmosTokens,
    solanaTokens,
    bitcoinTokens,
    suiTokens,
    xrplTokens,
    stellarTokens,
    cantonTokens,
  } = useMemo(() => {
    return tokens?.reduce(
      (acc, token) => {
        switch (token.type) {
          case ChainType.EVM:
            acc.evmTokens.push(token);
            break;
          case ChainType.COSMOS:
            acc.cosmosTokens.push(token);
            break;
          case ChainType.SOLANA:
            acc.solanaTokens.push(token);
            break;
          case ChainType.BTC:
            acc.bitcoinTokens.push(token);
            break;
          case ChainType.SUI:
            acc.suiTokens.push(token);
            break;
          case ChainType.XRPL:
            acc.xrplTokens.push(token);
            break;
          case ChainType.STELLAR:
            acc.stellarTokens.push(token);
            break;
          case ChainType.CANTON:
            acc.cantonTokens.push(token);
            break;
        }

        return acc;
      },
      {
        evmTokens: [] as Token[],
        cosmosTokens: [] as Token[],
        solanaTokens: [] as Token[],
        bitcoinTokens: [] as Token[],
        suiTokens: [] as Token[],
        xrplTokens: [] as Token[],
        stellarTokens: [] as Token[],
        cantonTokens: [] as Token[],
      }
    );
  }, [tokens]);

  const findToken = useCallback(
    (address?: string, chainId?: string) => {
      if (!address || !chainId) return undefined;
      return tokens?.find(
        (t) => t.address === address && t.chainId === chainId
      );
    },
    [tokens]
  );

  return {
    tokens,
    findToken,
    evmTokens,
    solanaTokens,
    cosmosTokens,
    bitcoinTokens,
    suiTokens,
    xrplTokens,
    stellarTokens,
    cantonTokens,
  };
};
